import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, authUrl, fmtPrice } from '../../lib/api';
import { ExternalLink, FileDown, ShoppingBag } from 'lucide-react';

const Block = ({ over, title, children, testid }) => (
  <section className="border border-stilo-ink/10 bg-white p-6 lg:p-10" data-testid={testid}>
    <p className="overline">{over}</p>
    {title && <h3 className="font-serif text-3xl mt-2 mb-6">{title}</h3>}
    {children}
  </section>
);

export const ProposalView = ({ cid, p }) => (
  <div className="space-y-6" data-testid="proposal-view">
    <div className="bg-stilo-ink text-stilo-bg p-8 lg:p-12 flex flex-wrap justify-between items-end gap-6">
      <div>
        <p className="overline !text-stilo-stone">Tu propuesta de diseño</p>
        <h2 className="font-serif text-4xl lg:text-5xl mt-3 font-light">Tu espacio, <em className="text-stilo-gold">con alma.</em></h2>
      </div>
      <a href={authUrl(`/consultations/${cid}/proposal/pdf`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 bg-stilo-bg text-stilo-ink text-sm tracking-[0.18em] uppercase hover:bg-stilo-beige transition-colors" data-testid="proposal-download-pdf">
        <FileDown className="w-4 h-4" strokeWidth={1.5} /> Descargar PDF
      </a>
    </div>

    {p.notes && (
      <Block over="Mis notas y recomendaciones" testid="view-notes">
        <p className="text-stilo-ink2 leading-relaxed whitespace-pre-wrap mt-4">{p.notes}</p>
      </Block>
    )}

    {(p.moodboard_url || p.moodboard_images.length > 0) && (
      <Block over="Moodboard" title="La atmósfera de tu espacio" testid="view-moodboard">
        {p.moodboard_url && (
          <a href={p.moodboard_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 btn-stilo mb-6" data-testid="view-moodboard-link">
            <ExternalLink className="w-4 h-4" strokeWidth={1.5} /> Ver moodboard completo
          </a>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {p.moodboard_images.map(fid => <img key={fid} src={authUrl(`/files/${fid}`)} alt="" className="w-full aspect-[4/5] object-cover" />)}
        </div>
      </Block>
    )}

    {p.palette.length > 0 && (
      <Block over="Paleta de color" title="Los tonos de tu propuesta" testid="view-palette">
        <div className="flex flex-wrap gap-6">
          {p.palette.map((c, i) => (
            <div key={i}>
              <div className="w-24 h-24 border border-stilo-ink/10" style={{ backgroundColor: c.hex }} />
              <p className="text-sm mt-2">{c.name}</p>
              <p className="text-xs text-stilo-ink3 uppercase">{c.hex}</p>
            </div>
          ))}
        </div>
      </Block>
    )}

    {p.items.length > 0 && <ShoppingList cid={cid} p={p} />}
  </div>
);

const ShoppingList = ({ cid, p }) => {
  const isStilo = (url) => { try { return new URL(url).hostname.replace(/^www\./, '').startsWith('stilodecor.'); } catch { return false; } };
  const [sel, setSel] = useState(() => new Set(p.items.filter(i => isStilo(i.url)).map(i => i.id)));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const chosen = p.items.filter(i => sel.has(i.id));
  const external = p.items.filter(i => !isStilo(i.url));
  const total = chosen.reduce((s, i) => s + i.price * Math.max(1, i.qty), 0);

  const goCart = async () => {
    const win = window.open('', '_blank');
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${cid}/proposal/cart`, { item_ids: [...sel] });
      if (r.data.skipped.length) toast.message(`No pude añadir: ${r.data.skipped.map(s => s.title).join(', ')}. Ábrelos desde su enlace.`);
      if (r.data.url) { if (win) win.location.href = r.data.url; else window.location.href = r.data.url; } else win?.close();
    } catch {
      win?.close(); toast.error('No se pudo preparar el carrito');
    } finally { setBusy(false); }
  };

  return (
    <Block over="Shopping list · Stilo Decor" title="Las piezas que elegiría para ti" testid="view-shopping-list">
      <p className="text-sm text-stilo-ink2 -mt-3 mb-4">Marca las piezas que quieras y las llevo a tu carrito de Stilo Decor con tu descuento aplicado.</p>
      {p.items.map(it => (
        <div key={it.id} className="flex gap-4 items-center border-b border-stilo-ink/10 py-4" data-testid={`view-item-${it.id}`}>
          {isStilo(it.url)
            ? <input type="checkbox" checked={sel.has(it.id)} onChange={() => toggle(it.id)} className="w-4 h-4 accent-[#B78E58]" aria-label={`Elegir ${it.title}`} data-testid={`view-item-check-${it.id}`} />
            : <span className="w-4" />}
          {it.image && <img src={it.image} alt={it.title} className="w-20 h-20 object-cover bg-stilo-beige" />}
          <div className="flex-1 min-w-0">
            <a href={it.url} target="_blank" rel="noreferrer" className="font-serif text-xl hover:text-stilo-gold transition-colors">{it.title}</a>
            {it.note && <p className="text-sm text-stilo-ink2">{it.note}</p>}
            <p className="text-xs text-stilo-ink3 mt-1">Cantidad: {it.qty}{!isStilo(it.url) && ' · Tienda externa: cómpralo desde su enlace'}</p>
          </div>
          <p className="font-medium whitespace-nowrap">{fmtPrice(it.price * Math.max(1, it.qty))}</p>
        </div>
      ))}
      <div className="flex flex-wrap justify-between items-center gap-4 mt-6">
        <div>
          <p className="overline">Total de lo elegido</p>
          <p className="font-serif text-3xl" data-testid="view-total">{fmtPrice(total)}</p>
          {chosen.length !== p.items.length && <p className="text-xs text-stilo-ink3">Lista completa: {fmtPrice(p.total)}{external.length > 0 ? ` (incluye ${external.length} de otras tiendas)` : ''}</p>}
        </div>
        <button onClick={goCart} disabled={busy || chosen.length === 0} className="btn-stilo disabled:opacity-50" data-testid="view-go-cart">
          <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> {busy ? 'Preparando…' : `Añadir al carrito (${chosen.length})`}
        </button>
      </div>
      {p.include_discount && p.discount_code && (
        <div className="mt-8 bg-stilo-beige p-6 flex flex-wrap justify-between items-center gap-4" data-testid="view-discount">
          <div>
            <p className="overline">Tu 10% en Stilo Decor</p>
            <p className="font-serif text-3xl mt-1 tracking-wide">{p.discount_code}</p>
          </div>
          <p className="text-xs text-stilo-ink2 max-w-xs">Va aplicado automáticamente al ir al carrito desde aquí.</p>
        </div>
      )}
    </Block>
  );
};
